import { Skeleton } from "@/components/shared/Skeleton";
import { SlideThumbnailBody } from "@/components/slides/list/SlideThumbnail";
import { CheckCircle } from "@/components/teacher/CheckCircle";
import { format, useT } from "@/i18n";
import { CANVAS_SIZE } from "@/lib/constants/slides";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import type { SlideFields } from "@/types/Slide";
import { resolveSlideTemplate } from "@ai-teach/types";

const GRID_CLASS =
  "grid grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]";

export interface LessonSlideGridItem {
  slide: SlideFields;
  /** Subslides that ride along with this slide: shown attached, never picked on their own. */
  children?: SlideFields[];
  /** Its place in the lesson. Defaults to its place in the list, which a filtered list breaks. */
  number?: number;
}

interface LessonSlideGridProps {
  items: LessonSlideGridItem[];
  /** What picking a tile means is the caller's: the lesson page opens it, the picker selects it. */
  onPickSlide: (slideId: string) => void;
  selectedIds?: ReadonlySet<string>;
}

/** Every slide of a lesson as its rendered self, so a name never has to stand in for a slide. */
export function LessonSlideGrid({ items, onPickSlide, selectedIds }: LessonSlideGridProps) {
  const t = useT();

  return (
    <div className={GRID_CLASS}>
      {items.map(({ slide, children = [], number }, index) => {
        const isSelected = selectedIds?.has(slide.id) ?? false;

        return (
          <div
            key={slide.id}
            className={`app-card app-card-link group relative overflow-hidden ${
              isSelected ? "ring-2 ring-app-ink" : ""
            }`}
          >
            {/* An overlay button rather than a wrapping one: a thumbnail is flow content, not a label. */}
            <button
              type="button"
              onClick={() => onPickSlide(slide.id)}
              aria-pressed={selectedIds ? isSelected : undefined}
              aria-label={format(t["slide.gridItem"], {
                name: slide.name,
                number: number ?? index + 1,
              })}
              className="absolute inset-0 z-10 rounded-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-app-ink"
            />

            <div
              // z-0 pins the whole thumbnail below the overlay button: templates layer freely inside.
              className={`@container ${CANVAS_SIZE.freeform.aspectRatioClass} relative z-0 overflow-hidden bg-app-sky-soft`}
            >
              <SlideThumbnailBody slide={slide} />
            </div>

            {selectedIds && (
              <CheckCircle
                checked={isSelected}
                className="pointer-events-none absolute right-2 top-2 z-[11] shadow-app-card"
              />
            )}

            <div className="border-t border-gray-100 p-3">
              <p className="flex items-baseline gap-1.5">
                <span className="app-label">{number ?? index + 1}</span>
                <span className="min-w-0 flex-1 truncate font-kumbh font-bold text-app-ink">
                  {slide.name}
                </span>
              </p>
              <p className="app-label mt-0.5">{getTemplateLabel(resolveSlideTemplate(slide))}</p>

              {children.length > 0 && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <p className="app-label">{t["slide.playsWith"]}</p>
                  <div className="mt-1.5 flex gap-1.5 overflow-x-auto">
                    {children.map((child) => (
                      <span
                        key={child.id}
                        title={child.name}
                        className={`relative z-0 block w-12 shrink-0 overflow-hidden rounded-lg bg-app-sky-soft ${CANVAS_SIZE.freeform.aspectRatioClass}`}
                      >
                        <SlideThumbnailBody slide={child} />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LessonSlideGridSkeleton({ count }: { count: number }) {
  return (
    <div className={GRID_CLASS}>
      {Array.from({ length: count }, (_, index) => index).map((key) => (
        <div key={key} className="app-card overflow-hidden">
          <Skeleton className={`w-full ${CANVAS_SIZE.freeform.aspectRatioClass} rounded-none`} />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
